import {
    BarElement,
    CategoryScale,
    Chart,
    ChartData,
    ChartOptions,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { TFunction } from "i18next";
import * as React from "react";
import { Bar } from "react-chartjs-2";
import { Translation } from "react-i18next";

import style from "./chart.scss";
import { commonFunctions, Units } from "./CommonFunctions";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { DashboardDataList } from "domain/dashboard";
import { resolveColor } from "utils/commonFunctions";

interface ChartProps {
    displayLegend?: boolean;
    widgetData: DashboardDataList[];
    dataFetched: boolean;
    convertToTonnes: boolean;
}

interface ChartState {
    data?: [];
    displayLegend?: boolean;
}

type Props = ChartProps;

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

class TotalProcessedDevicesChart extends React.Component<Props, ChartState> {
    public static defaultProps = {
        displayLegend: false,
        convertToTonnes: false,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            data: [],
            displayLegend: this.props.displayLegend,
        };
    }

    public getChartData(): ChartData<"bar"> {
        const monthData: number[] = [];
        Object.values(this.props.widgetData).forEach((dashboardData) => {
            const monthAmount =
                dashboardData.content.Laptop.weight +
                dashboardData.content.Desktop.weight +
                dashboardData.content.Server.weight +
                dashboardData.content["Loose drive"].weight +
                dashboardData.content.Smartphone.weight +
                dashboardData.content.Tablet.weight;
            monthData.push(this.props.convertToTonnes ? monthAmount / 1000 : monthAmount);
        });

        return {
            datasets: [
                {
                    data: monthData,
                    backgroundColor: resolveColor("--app-chart-chartFirstSet"),
                },
            ],
            labels: commonFunctions.fetchMonths(this.props.widgetData),
        };
    }

    public render(): JSX.Element {
        if (!this.props.dataFetched) {
            return (
                <div className={style.loaderContainer}>
                    <LoadingIndicator />
                </div>
            );
        }
        const displayedValuesAndUnits = commonFunctions.getDisplayedValueAndUnit(
            commonFunctions.computeTotalNumberOfErasedDevices(this.props.widgetData),
            commonFunctions.computeAverageWeightProcessPerDay(this.props.widgetData)
        );

        return (
            <Translation>
                {(t) => (
                    <div className={style.chartContainer}>
                        <div className={style.topSummaryContainer}>
                            <TextBlock>
                                <span className={style.summaryCount}>
                                    {t("EsgDashboardView.totalOfProcessedDevicesChart.totalOfProcessedDevices", {
                                        totalOfProcessedDevices: displayedValuesAndUnits.total.toFixed(),
                                        unit: t(displayedValuesAndUnits.totalUnit),
                                    })}
                                </span>
                                <br />
                                {t("EsgDashboardView.referencedMonths", {
                                    firstMonth: commonFunctions.computeFirstMonth(this.props.widgetData),
                                    lastMonth: commonFunctions.computeLastMonth(this.props.widgetData),
                                })}
                                <br />
                                {t("EsgDashboardView.averagePerDay", {
                                    averagePerDay: displayedValuesAndUnits.average?.toFixed(2),
                                    unit: displayedValuesAndUnits.averageUnit
                                        ? t(displayedValuesAndUnits.averageUnit)
                                        : "",
                                    numberOfMonths: commonFunctions.fetchMonths(this.props.widgetData).length,
                                })}
                            </TextBlock>
                        </div>
                        <Bar
                            data={this.getChartData()}
                            options={getChartOptions(
                                resolveColor("--app-chart-gridLineColor"),
                                resolveColor("--app-chart-textColor"),
                                this.props.convertToTonnes,
                                displayedValuesAndUnits.totalUnit,
                                t
                            )}
                        />
                    </div>
                )}
            </Translation>
        );
    }
}

function getChartOptions(
    gridLineColor: string,
    textColor: string,
    convert: boolean,
    unit: Units,
    t: TFunction
): ChartOptions<"bar"> {
    return {
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: textColor,
                },
            },
            y: {
                display: true,
                grid: {
                    color: gridLineColor,
                },
                beginAtZero: true,
                ticks: {
                    maxTicksLimit: 6,
                    callback(n: number) {
                        return commonFunctions.toYAxisValueWithUnit(n, convert, unit, t(unit));
                    },
                    color: textColor,
                },
            },
        },
        elements: {
            point: {
                radius: 0,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
            },
            datalabels: {
                display: false,
            },
        },
    };
}

export default TotalProcessedDevicesChart;
